import { config } from "@/utils/config"
import { ApiRequestError, fetchAPI, handleResponse } from "@/utils"

export type ViewerApiVersion = "v1" | "v2"

export interface LensOption {
  code: string
  order: number
  active: boolean
}

export interface ViewerRuntimeConfig {
  apiVersion?: ViewerApiVersion
  caseSearch?: ViewerSearchColumn[]
}

export interface ViewerSearchColumn {
  fieldPath: string
  displayName: string
  searchable?: boolean
  filterable?: boolean
  isCollection?: boolean
  filterConfig?: {
    type?: string
    description?: string
    maxLength?: number
    placeholder?: string
  }
}

export interface CaseSearchRequest extends Record<string, unknown> {
  searchText?: string
  searchMode?: "all" | "any"
  searchFields?: string[]
  filter?: string
  lensCode: string
  retentionFilter: "All"
  expiryDate: null
  orderBy: string[]
  select: string[]
}

export interface CaseResult {
  id: string
  caseReference: string
}

export interface PagedResults<T> {
  items: T[]
  page: number
  totalCount: number
}

export interface TestFile {
  fileReference: string
  displayName: string
  fileType?: string
  extension?: string
  edxProcessingStatus?: string | null
  edxRecordProcessingClass?: string
  passwordStatus?: string
  aiClassificationGroup?: string
  aiClassification?: string
  caseReference: string
  sessionId?: string
  runs?: Record<string, TestFileRun>
}

export interface TestFileAttempt {
  status?: TestFileStatus
  statusMessage?: string
  results?: TestMatch[]
  error?: string
}

export interface TestFileRun extends TestFileAttempt {
  previousAttempts?: TestFileAttempt[]
}

export type TestFileStatus =
  | "Loading session"
  | "Searching"
  | "Waiting for results"
  | "Rendering matches"
  | "Success"
  | "Excluded"
  | "Skipped"
  | "Cancelled"
  | "Failed"

export interface TestMatch {
  id: string
  text: string
  context: string
  pageIndex: number
  pageData: { width: number; height: number }
  boundingRectangle: { x: number; y: number; width: number; height: number }
  imageUrl?: string
}

interface ViewingSession {
  viewingSessionId: string
}

interface ViewerSearchResult extends Omit<TestMatch, "imageUrl"> {
  index?: string
}

interface ViewerSearchPage {
  results: ViewerSearchResult[]
  continueAfter?: number
  continueToken?: string
}

function viewerApi(path: string) {
  return `${config.viewerApiUrl.replace(/\/$/, "")}${path}`
}

function contentUrl(path: string) {
  return `${config.viewerContentUrl.replace(/\/$/, "")}${path}`
}

export async function loadViewerRuntime(client: string, brand: string, signal?: AbortSignal) {
  const lenses = await fetchAPI<LensOption[]>(
    viewerApi(`/api/brandLens/${encodeURIComponent(client)}/${encodeURIComponent(brand)}`),
    { signal },
  ).then(handleResponse)
  const activeLens = [...lenses].sort((a, b) => a.order - b.order).find((lens) => lens.active)
  const lensCode = activeLens?.code ?? "def"

  const runtime = await fetchAPI<ViewerRuntimeConfig>(
    viewerApi(
      `/api/brandclientuiconfig/${encodeURIComponent(client)}/${encodeURIComponent(brand)}?lensCode=${encodeURIComponent(lensCode)}`,
    ),
    { signal },
  ).then(handleResponse)

  return { lensCode, apiVersion: runtime.apiVersion ?? "v1", caseSearch: runtime.caseSearch ?? [] }
}

export async function searchCases(
  client: string,
  brand: string,
  request: CaseSearchRequest,
  page: number,
  pageSize: number,
  signal?: AbortSignal,
) {
  return fetchAPI<PagedResults<CaseResult>>(
    viewerApi(`/api/caseSearch/${encodeURIComponent(client)}/${encodeURIComponent(brand)}/${page}/${pageSize}/search`),
    {
      method: "POST",
      body: request,
      signal,
    },
  ).then(handleResponse)
}

export async function getCaseFiles(
  client: string,
  brand: string,
  caseReference: string,
  signal?: AbortSignal,
): Promise<TestFile[]> {
  const files = await fetchAPI<Array<Omit<TestFile, "caseReference">>>(
    viewerApi(
      `/api/metadata/${encodeURIComponent(client)}/${encodeURIComponent(brand)}/${encodeURIComponent(caseReference)}/file%20list`,
    ),
    { signal },
  ).then(handleResponse)

  return files.map((file) => ({ ...file, caseReference, runs: {} }))
}

export async function createViewingSession(
  client: string,
  brand: string,
  file: TestFile,
  signal?: AbortSignal,
): Promise<string> {
  const session = await fetchAPI<ViewingSession>(
    viewerApi(
      `/api/binary/view/${encodeURIComponent(client)}/${encodeURIComponent(brand)}/${encodeURIComponent(file.caseReference)}/${encodeURIComponent(file.fileReference)}/false/1?sessionStartOption=joinExisting`,
    ),
    { signal },
  ).then(async (response) => {
    await throwViewerError(response)
    return handleResponse(response)
  })

  return session.viewingSessionId
}

async function throwViewerError(response: { ok: boolean; status: number; json: () => Promise<unknown> }) {
  if (response.ok) return
  let body: unknown
  try {
    body = await response.json()
  } catch (error) {
    if (!(error instanceof SyntaxError)) throw error
  }
  const message =
    typeof body === "object" && body != null && "message" in body && typeof body.message === "string"
      ? body.message.trim()
      : ""
  throw new ApiRequestError(message === "" ? "An unexpected error occurred" : message, response.status, body)
}

async function fetchContent<T>(path: string, init?: RequestInit, signal?: AbortSignal): Promise<T> {
  const response = await fetch(contentUrl(path), {
    ...init,
    signal,
    headers: { "Content-Type": "application/json", ...init?.headers },
  })

  await throwViewerError(response)

  return (await response.json()) as T
}

export async function submitDocumentSearch(
  client: string,
  sessionId: string,
  apiVersion: ViewerApiVersion,
  searchTerms: Array<{ pattern: string }>,
  signal?: AbortSignal,
): Promise<string> {
  const searchRequest = {
    searchTerms: searchTerms.map(({ pattern }) => ({ type: "simple", pattern, caseSensitive: false })),
  }
  const prefix = apiVersion === "v1" ? `/${encodeURIComponent(client)}` : ""
  const result = await fetchContent<{ processId: string }>(
    `${prefix}/v2/viewingSessions/${encodeURIComponent(sessionId)}/searchTasks`,
    { method: "POST", body: JSON.stringify({ input: searchRequest }) },
    signal,
  )

  return result.processId
}

export async function getDocumentSearchResults(
  client: string,
  processId: string,
  apiVersion: ViewerApiVersion,
  continueToken?: string,
  signal?: AbortSignal,
): Promise<ViewerSearchPage> {
  const prefix = apiVersion === "v1" ? `/${encodeURIComponent(client)}/v2/searchTasks` : "/v2/searchTasks"
  const token = continueToken == null ? "" : `?continueToken=${encodeURIComponent(continueToken)}`

  if (apiVersion === "v1") {
    return fetchContent<ViewerSearchPage>(
      `${prefix}/${encodeURIComponent(processId)}/results${token}`,
      undefined,
      signal,
    )
  }

  const response = await fetchContent<{
    results: Array<{ result: Omit<ViewerSearchResult, "text" | "pageIndex"> & { txt: string; pageNumber: number } }>
    continueAfter?: number
    continueToken?: string
  }>(`${prefix}/${encodeURIComponent(processId)}${token}`, undefined, signal)

  return {
    ...response,
    results: response.results.map(({ result }) => ({
      ...result,
      text: result.txt,
      pageIndex: result.pageNumber - 1,
    })),
  }
}

export function getPageImageUrl(client: string, sessionId: string, pageIndex: number, apiVersion: ViewerApiVersion) {
  if (apiVersion === "v1") {
    return contentUrl(
      `/${encodeURIComponent(client)}/Page/q/${pageIndex}?DocumentID=u${encodeURIComponent(sessionId)}&Scale=1&Quality=85&ContentType=jpeg`,
    )
  }

  return contentUrl(`/Page/q/${pageIndex + 1}?DocumentID=${encodeURIComponent(sessionId)}&Scale=1&Content=jpeg`)
}

export async function loadPageImage(imageUrl: string, signal?: AbortSignal): Promise<HTMLImageElement> {
  const image = new Image()
  image.crossOrigin = "anonymous"

  await new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      image.onload = null
      image.onerror = null
      signal?.removeEventListener("abort", onAbort)
    }
    const onAbort = () => {
      cleanup()
      image.removeAttribute("src")
      reject(new DOMException("Aborted", "AbortError"))
    }

    image.onload = () => {
      cleanup()
      resolve()
    }
    image.onerror = () => {
      cleanup()
      reject(new Error("Could not render matched page image"))
    }
    if (signal?.aborted === true) {
      onAbort()
      return
    }
    signal?.addEventListener("abort", onAbort, { once: true })
    image.src = imageUrl
  })

  return image
}

/** The caller owns the preview URL and must revoke it when its attempt is discarded. */
export async function createMatchSnapshot(
  image: HTMLImageElement,
  result: ViewerSearchResult,
  signal?: AbortSignal,
): Promise<string> {
  signal?.throwIfAborted()
  const scale = image.naturalWidth / result.pageData.width
  const heightAdjustment = ((result.pageData.height * scale) / 100) * 10
  const sourceY = Math.max(0, result.boundingRectangle.y * scale - heightAdjustment / 2)
  const sourceHeight = Math.min(
    image.naturalHeight - sourceY,
    result.boundingRectangle.height * scale + heightAdjustment,
  )
  const targetWidth = Math.min(image.naturalWidth, Math.max(480, window.innerWidth - 440))
  const targetHeight = Math.max(1, (sourceHeight / image.naturalWidth) * targetWidth)
  const canvas = document.createElement("canvas")
  canvas.width = targetWidth
  canvas.height = targetHeight

  const context = canvas.getContext("2d")
  if (context == null) throw new Error("Could not create match preview")

  context.drawImage(image, 0, sourceY, image.naturalWidth, sourceHeight, 0, 0, targetWidth, targetHeight)
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((result) => {
      canvas.width = 0
      canvas.height = 0
      if (result == null) reject(new Error("Could not encode match preview"))
      else resolve(result)
    }, "image/png")
  })

  signal?.throwIfAborted()
  return URL.createObjectURL(blob)
}
